import React, { useState, useEffect } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import { BaseFormFields, useBaseForm } from "./BaseFormFields";
import * as api from "../../lib/api";

const RESOURCE_TYPES = [
    { id: "network_share", label: "Partage réseau", icon: "bg-blue-400" },
    { id: "server", label: "Serveur", icon: "bg-purple-500" },
    { id: "application", label: "Application", icon: "bg-green-400" },
    { id: "physical", label: "Accès physique", icon: "bg-orange-400" },
];

const ACCESS_LEVELS = [
    { id: "read", label: "Lecture seule", icon: "bg-green-400" },
    { id: "write", label: "Écriture seule", icon: "bg-yellow-400" },
    { id: "read_write", label: "Lecture et écriture", icon: "bg-orange-400" },
    { id: "admin", label: "Administration", icon: "bg-red-500" },
];

const ACCESS_DURATIONS = [
    { id: "permanent", label: "Permanent", icon: "bg-blue-400" },
    { id: "temporary", label: "Temporaire", icon: "bg-amber-400" },
];

export function AccessRequestForm({ onSubmit, initialData, readOnly, submitting, onTitleChange, allowedPriorities, ticketType, onTypeChange }) {
    const baseForm = useBaseForm("medium");
    const [resourceType, setResourceType] = useState("network_share");
    const [resourceId, setResourceId] = useState("");
    const [accessLevel, setAccessLevel] = useState("read");
    const [accessDuration, setAccessDuration] = useState("permanent");
    const [accessEndDate, setAccessEndDate] = useState("");
    const [justification, setJustification] = useState("");
    const [resources, setResources] = useState([]);

    useEffect(() => {
        loadResources();
    }, []);

    const loadResources = async () => {
        try {
            const data = await api.getResourcesCatalog();
            setResources(data);
        } catch (error) {
            console.error("Failed to load resources:", error);
        }
    };

    const selectedResource = resources.find((r) => r.id === resourceId);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit({
            title: baseForm.title || `Demande d'accès - ${selectedResource?.name || resourceType}`,
            description: baseForm.description,
            priority: baseForm.priority,
            dynamic_fields: {
                resource_type: resourceType,
                resource_id: resourceId,
                resource_name: selectedResource?.name || "",
                access_level: accessLevel,
                access_duration: accessDuration,
                access_end_date: accessDuration === "temporary" ? accessEndDate : null,
                business_justification: justification,
            },
        });
    };

    return (
        <form onSubmit={handleSubmit}>
            <BaseFormFields
                ticketType={ticketType}
                onTypeChange={onTypeChange}
                {...baseForm}
                onTitleChange={onTitleChange}
                    allowedPriorities={allowedPriorities}
                titlePlaceholder="Ex: Accès au partage RH"
                descriptionPlaceholder="Contexte supplémentaire..."
            >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Type de ressource</Label>
                        <Select value={resourceType} onValueChange={setResourceType}>
                            <SelectTrigger data-testid="resource-type">
                                <SelectValue>
                                    {(() => { const t = RESOURCE_TYPES.find((t) => t.id === resourceType); return t ? <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${t.icon}`} />{t.label}</span> : null; })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {RESOURCE_TYPES.map((t) => (
                                    <SelectItem key={t.id} value={t.id}>
                                        <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${t.icon}`} />{t.label}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Ressource</Label>
                        <Select value={resourceId} onValueChange={setResourceId}>
                            <SelectTrigger data-testid="resource-select">
                                <SelectValue placeholder="Sélectionner une ressource" />
                            </SelectTrigger>
                            <SelectContent>
                                {resources
                                    .filter((r) => r.resource_type === resourceType)
                                    .map((r) => (
                                        <SelectItem key={r.id} value={r.id}>
                                            {r.name}
                                        </SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="form-group">
                        <Label className="form-label">Niveau d'accès</Label>
                        <Select value={accessLevel} onValueChange={setAccessLevel}>
                            <SelectTrigger data-testid="access-level">
                                <SelectValue>
                                    {(() => { const l = ACCESS_LEVELS.find((l) => l.id === accessLevel); return l ? <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${l.icon}`} />{l.label}</span> : null; })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {ACCESS_LEVELS.map((l) => (
                                    <SelectItem key={l.id} value={l.id}>
                                        <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${l.icon}`} />{l.label}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div className="form-group">
                        <Label className="form-label">Durée</Label>
                        <Select value={accessDuration} onValueChange={setAccessDuration}>
                            <SelectTrigger data-testid="access-duration">
                                <SelectValue>
                                    {(() => { const d = ACCESS_DURATIONS.find((d) => d.id === accessDuration); return d ? <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${d.icon}`} />{d.label}</span> : null; })()}
                                </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                                {ACCESS_DURATIONS.map((d) => (
                                    <SelectItem key={d.id} value={d.id}>
                                        <span className="flex items-center gap-2.5"><span className={`w-2.5 h-2.5 rounded-full ${d.icon}`} />{d.label}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {accessDuration === "temporary" && (
                    <div className="form-group">
                        <Label className="form-label">Date de fin d'accès</Label>
                        <Input
                            type="date"
                            value={accessEndDate}
                            onChange={(e) => setAccessEndDate(e.target.value)}
                            data-testid="access-end-date"
                        />
                    </div>
                )}

                <div className="form-group">
                    <Label className="form-label">Justification métier</Label>
                    <Textarea
                        value={justification}
                        onChange={(e) => setJustification(e.target.value)}
                        placeholder="Pourquoi avez-vous besoin de cet accès ?"
                        data-testid="justification"
                    />
                </div>
            </BaseFormFields>

            <div className="mt-6 flex justify-end">
                <Button type="submit" disabled={!baseForm.title || submitting} data-testid="submit-ticket">
                    {submitting ? "Création..." : "Soumettre le ticket"}
                </Button>
            </div>
        </form>
    );
}
